// Rotación de producto frente a su subtipo, reposición, acciones comerciales
// sugeridas y tendencias (CEO, 22/10/2026). Lógica pura; los datos los trae
// server/lib/rotacion-db.ts.
//
// «Venta media» es la misma que enseña el listado de productos:
//   - clientes con ventas SEMANALES (GAME): unidades de las 4 últimas semanas / 4;
//   - clientes con ventas MENSUALES (TICNOVA): media de los 3 últimos meses con
//     venta (un mes a 0 no entra en el divisor, decisión del CEO).
// El periodo anterior (para las tendencias) son las 4 semanas / 3 meses previos.
//
// Para COMPARAR con el subtipo (media del subtipo, ranking, flecha y acciones
// propuestas) se usa la «venta media con stock» (CEO, 22/10/2026): solo los
// periodos en que el stock del producto estaba por encima de su stock mínimo
// (un producto roto no vende por falta de stock, no por falta de demanda), y
// la media del subtipo solo con productos VIGENTES.

export const ACCIONES_COMERCIALES = ['MANTENER', 'OFERTA_MEJORAR_ROTACION', 'LIQUIDAR'] as const;
export type AccionComercial = (typeof ACCIONES_COMERCIALES)[number];
export const ACCION_LABEL: Record<AccionComercial, string> = {
  MANTENER: 'Mantener',
  OFERTA_MEJORAR_ROTACION: 'Oferta · mejorar rotación',
  LIQUIDAR: 'Liquidar',
};

export type UnidadVenta = 'semana' | 'mes';
/** Semanas por mes para pasar de venta mensual a semanal (52/12). */
export const SEMANAS_POR_MES = 52 / 12;

export type MetricaProducto = {
  id: string;
  sku: string;
  nombre: string;
  estado: string;
  accion_comercial: AccionComercial;
  categoria_id: string | null;
  categoria_nombre: string | null;
  subcategoria_id: string | null;
  subcategoria_nombre: string | null;
  stock_actual: number | null;
  /** Unidades pedidas pendientes de llegar (misma regla que el listado). */
  pendiente: number;
  media: number | null;
  media_prev: number | null;
  /** Venta media solo de los periodos con stock por encima del mínimo (null si ninguno). */
  media_con_stock: number | null;
  stock_minimo_producto: number | null;
  stock_minimo_condicion: number | null;
  condicion_texto: string | null;
};

const ESTADOS_ACTIVOS = new Set(['VIGENTE', 'EN_FABRICACION', 'BUSCAR_SUSTITUTO']);
export const esActivo = (estado: string) => ESTADOS_ACTIVOS.has(estado);

export type Flecha = 'arriba' | 'neutro' | 'abajo';

/** Compara la venta media del producto con la de su subtipo (banda neutra ±banda%). */
export function flechaFrente(media: number | null, referencia: number | null, bandaPct: number): Flecha | null {
  if (referencia == null || !(referencia > 0)) return null;
  const m = Math.max(0, Number(media) || 0);
  const ratio = m / referencia;
  if (ratio > 1 + bandaPct / 100) return 'arriba';
  if (ratio < 1 - bandaPct / 100) return 'abajo';
  return 'neutro';
}

export type EstadisticaSubtipo = {
  subcategoria_id: string;
  /** Media de la venta media de los productos del subtipo que venden. */
  media: number;
  /** Productos que venden, ordenados de más a menos venta media. */
  ranking: string[];
};

/**
 * Media y ranking por subtipo: solo productos VIGENTES con venta media con
 * stock > 0 (periodos con el stock por encima del mínimo).
 */
export function estadisticasSubtipo(filas: Pick<MetricaProducto, 'id' | 'subcategoria_id' | 'estado' | 'media_con_stock'>[]): Map<string, EstadisticaSubtipo> {
  const grupos = new Map<string, { id: string; media: number }[]>();
  for (const f of filas) {
    const m = Number(f.media_con_stock) || 0;
    if (!f.subcategoria_id || f.estado !== 'VIGENTE' || m <= 0) continue;
    grupos.set(f.subcategoria_id, [...(grupos.get(f.subcategoria_id) ?? []), { id: f.id, media: m }]);
  }
  const out = new Map<string, EstadisticaSubtipo>();
  for (const [sub, items] of grupos) {
    const orden = items.slice().sort((a, b) => b.media - a.media);
    out.set(sub, {
      subcategoria_id: sub,
      media: items.reduce((s, x) => s + x.media, 0) / items.length,
      ranking: orden.map((x) => x.id),
    });
  }
  return out;
}

export type StockMinimo = { valor: number; fuente: 'producto' | 'condicion' | 'global'; detalle: string | null };

/** Escalera del stock mínimo: ficha → condición comercial (subtipo › tipo › marca) → global. */
export function stockMinimoDe(p: Pick<MetricaProducto, 'stock_minimo_producto' | 'stock_minimo_condicion' | 'condicion_texto'>, global: number): StockMinimo {
  if (p.stock_minimo_producto != null) return { valor: Number(p.stock_minimo_producto), fuente: 'producto', detalle: null };
  if (p.stock_minimo_condicion != null) return { valor: Number(p.stock_minimo_condicion), fuente: 'condicion', detalle: p.condicion_texto };
  return { valor: Math.max(0, Number(global) || 0), fuente: 'global', detalle: null };
}

export type Reposicion = {
  /** Venta semanal equivalente. */
  venta_semanal: number;
  /** Semanas que aguanta el stock actual hasta tocar el stock mínimo. */
  semanas_hasta_minimo: number;
  /** Unidades a pedir hoy para cubrir el plazo y acabar en el stock mínimo. */
  cantidad: number;
  /** Se rompe (llega al mínimo) antes de que llegue un pedido hecho hoy. */
  urgente: boolean;
};

/**
 * Punto de pedido: stock + pendiente < venta durante el plazo + stock mínimo.
 * Devuelve null si no hace falta reponer o el producto no vende.
 */
export function calcularReposicion(
  p: Pick<MetricaProducto, 'stock_actual' | 'pendiente' | 'media'>,
  unidad: UnidadVenta,
  plazoSemanas: number,
  stockMinimo: number,
): Reposicion | null {
  const media = Number(p.media) || 0;
  if (media <= 0) return null;
  const semanal = unidad === 'mes' ? media / SEMANAS_POR_MES : media;
  const stock = Math.max(0, Number(p.stock_actual) || 0);
  const disponible = stock + Math.max(0, Number(p.pendiente) || 0);
  const necesario = semanal * plazoSemanas + stockMinimo;
  if (disponible >= necesario) return null;
  const semanasHastaMinimo = Math.max(0, (stock - stockMinimo) / semanal);
  return {
    venta_semanal: Math.round(semanal * 10) / 10,
    semanas_hasta_minimo: Math.round(semanasHastaMinimo * 10) / 10,
    cantidad: Math.ceil(necesario - disponible),
    urgente: semanasHastaMinimo < plazoSemanas,
  };
}

export type AccionSugerida = { accion: AccionComercial; motivo: string };

/**
 * Sugerencia de estrategia comercial según la rotación frente al subtipo:
 *  - por debajo de la media (fuera de la banda neutra) → OFERTA · mejorar rotación;
 *  - por encima → MANTENER (si estaba en otra acción).
 * Sin venta pero con stock también es rotación baja. Solo productos activos.
 */
export function accionSugerida(
  p: Pick<MetricaProducto, 'estado' | 'accion_comercial' | 'media_con_stock' | 'stock_actual'>,
  mediaSubtipo: number | null,
  bandaPct: number,
): AccionSugerida | null {
  if (!esActivo(p.estado)) return null;
  // Sin ningún periodo con stock por encima del mínimo no se puede juzgar su rotación.
  if (p.media_con_stock == null) return null;
  const media = Number(p.media_con_stock) || 0;
  if (media <= 0 && !(Number(p.stock_actual) > 0)) return null;
  const f = flechaFrente(media, mediaSubtipo, bandaPct);
  if (f === 'abajo' && p.accion_comercial !== 'OFERTA_MEJORAR_ROTACION') {
    return { accion: 'OFERTA_MEJORAR_ROTACION', motivo: media > 0 ? 'Rota por debajo de la media de su subtipo' : 'Tiene stock y no vende' };
  }
  if (f === 'arriba' && p.accion_comercial !== 'MANTENER') {
    return { accion: 'MANTENER', motivo: 'Rota por encima de la media de su subtipo' };
  }
  return null;
}

export type Tendencia = { sentido: 'sube' | 'baja'; variacion_pct: number | null };

/** Variación de la venta media frente al periodo anterior, si supera el umbral. */
export function tendenciaDe(media: number | null, prev: number | null, umbralPct: number): Tendencia | null {
  const a = Number(media) || 0;
  const b = Number(prev) || 0;
  if (a <= 0 && b <= 0) return null;
  if (b <= 0) return { sentido: 'sube', variacion_pct: null };
  const v = ((a - b) / b) * 100;
  if (v >= umbralPct) return { sentido: 'sube', variacion_pct: Math.round(v) };
  if (v <= -umbralPct) return { sentido: 'baja', variacion_pct: Math.round(v) };
  return null;
}
